import type {
  CampaignLogicAction,
  CampaignLogicDetail,
  CampaignLogicSingle,
  LogicActionCondition,
  LogicConditionValue,
  SurveyElementJson,
} from '@coorum-research/shared-common/types'
import type { SurveyAnswerRecord } from '@coorum-research/shared-common/types/answer.type'
import { isArray } from 'es-toolkit/compat'
import { campaignLogicActionChoicePrioritySort, campaignLogicActionPrioritySort } from '../utils'

export function surveyLogic() {
  const evaluateCondition = (condition: LogicActionCondition, answerValue: SurveyAnswerRecord): boolean => {
    const { op, vars } = condition

    const evaluateVar = (v: LogicConditionValue): boolean => {
      const answer = answerValue[v.typeId]

      switch (v.questionType) {
      case 'text':
        switch (v.valueOp) {
        case 'equal':
          return answer === v.value
        case 'not_equal':
          return answer !== v.value
        case 'begins_with':
          return typeof answer === 'string' && answer.startsWith(v.value)
        case 'ends_with':
          return typeof answer === 'string' && answer.endsWith(v.value)
        case 'contains':
          return typeof answer === 'string' && answer.includes(v.value)
        case 'not_contains':
          return typeof answer === 'string' && !answer.includes(v.value)
        default:
          return false // Unknown valueOp for text
        }
      case 'comment':
        switch (v.valueOp) {
        case 'equal':
          return answer === v.value
        case 'not_equal':
          return answer !== v.value
        case 'begins_with':
          return typeof answer === 'string' && answer.startsWith(v.value)
        case 'ends_with':
          return typeof answer === 'string' && answer.endsWith(v.value)
        case 'contains':
          return typeof answer === 'string' && answer.includes(v.value)
        case 'not_contains':
          return typeof answer === 'string' && !answer.includes(v.value)
        default:
          return false // Unknown valueOp for text
        }
      case 'radiogroup':
        return v.valueOp === 'is' ? answer === v.value : answer !== v.value
      case 'dropdown':
        return v.valueOp === 'is' ? answer === v.value : answer !== v.value
      case 'checkbox':
        // imagepickerはcheckboxは複数選択がある
        if (isArray(answer) && answer.every(a => typeof a === 'string')) {
          return v.valueOp === 'is' ? answer.includes(v.value) : !answer.includes(v.value)
        } else {
          return v.valueOp === 'is' ? answer === v.value : answer !== v.value
        }
      case 'boolean':
        return v.valueOp === 'is' ? answer === v.value : answer !== v.value
      case 'imagepicker':
        // imagepickerはcheckboxは複数選択がある
        if (isArray(answer) && answer.every(a => typeof a === 'string')) {
          return v.valueOp === 'is' ? answer.includes(v.value) : !answer.includes(v.value)
        } else {
          return v.valueOp === 'is' ? answer === v.value : answer !== v.value
        }
      case 'rating':
        switch (v.valueOp) {
        case 'equal':
          return Number(answer) === v.value
        case 'not_equal':
          return Number(answer) !== v.value
        case 'lower_than':
          return Number(answer) < v.value
        case 'lower_equal_than':
          return Number(answer) <= v.value
        case 'greater_than':
          return Number(answer) > v.value
        case 'greater_equal_than':
          return Number(answer) >= v.value
        default:
          return false // Unknown valueOp for rating
        }
      default:
        return false
      }
    }

    switch (op) {
    case 'is':
      // isの時には、varsは1つだけ
      return evaluateVar(vars[0])
    case 'and':
      return vars.every(evaluateVar)
    case 'or':
      return vars.some(evaluateVar)
    case 'always':
      return true
    default:
      throw new Error('Unknown operator')
    }
  }

  /**
   * ex) 24.5%
   */
  const getProgressiveWidth = (allElements: SurveyElementJson[], currentPageIndex: number): string => {
    const total = allElements.length
    if (total >= 1) {
      const percentage = Math.ceil((currentPageIndex + 1) / total * 100)
      return `${percentage}%`
    } else {
      return '100%'
    }
  }

  const getJustNextElementIndex = (allElements: SurveyElementJson[], currentElement: SurveyElementJson): number => {
    const justNextElementIndex = allElements.findIndex(el => el.name === currentElement.name) + 1
    if (allElements.length === justNextElementIndex) {
      // 最後の要素の場合、-1を返す
      return -1
    } else {
      return justNextElementIndex
    }
  }

  /**
   * 与えられた条件と、回答のデータから、条件のdetail.toを探す
   */
  const evaluateLogic = (
    actions: CampaignLogicAction[] | undefined,
    answerValue: SurveyAnswerRecord,
    choiceValues?: string[],
  ): CampaignLogicDetail | undefined => {
    if (!actions) {
      return
    }

    // Find all matching actions(優先順位ソートしてから評価して探す)
    const matchingActions = campaignLogicActionPrioritySort(actions).filter((action) => {
      return evaluateCondition(action.condition, answerValue)
    })

    if (matchingActions.length === 0) {
      return undefined
    }

    if (matchingActions.length === 1 || !choiceValues || choiceValues.length === 0) {
      return matchingActions[0].detail
    }

    return campaignLogicActionChoicePrioritySort(matchingActions, choiceValues)[0].detail
  }

  const getCurrentModelLogic = (logics: CampaignLogicSingle[], currentElement: SurveyElementJson): CampaignLogicSingle | undefined => {
    return logics.find(logic => logic.ref === currentElement.name)
  }

  /**
   * 与えられた条件にマッチする、次のquestionのindexを返す
   */
  const getActionJumpQuestionIndex = (logics: CampaignLogicSingle[], allElements: SurveyElementJson[], currentElement: SurveyElementJson, answerValue: SurveyAnswerRecord): number => {
    const justNextElementIndex = getJustNextElementIndex(allElements, currentElement)
    const currentModelLogic = getCurrentModelLogic(logics, currentElement)
    const actionDetail = evaluateLogic(currentModelLogic?.actions, answerValue)

    if (!actionDetail) {
      // If matching action is not found, return the next element index
      return justNextElementIndex
    }

    const jumpType = actionDetail.to.type
    switch (jumpType) {
    case 'launcher_content_thanks':
      // マッチするquestionがない扱いにして、使う側で処理を委ねる。 index -1を返す
      // 「-1 === 全部回答した」
      // launcher_content_thanksだけを判断する部分で、サンクスページへの遷移を行う。
      return -1
    case 'question': {
      // Move to the target question
      const jumpTo = actionDetail.to.value
      const nextElementIndex = allElements.findIndex(el => el.name === jumpTo)

      if (nextElementIndex === -1) {
        return justNextElementIndex
      }

      return nextElementIndex
    }
    case 'default':
      return justNextElementIndex
    default:
      throw new Error('Unknown jump type')
    }
  }

  /**
   * 与えられた条件にマッチする、LauncherContentのidを返す
   * マッチするのがないならundefinedを返す -> default値はここで判断しないで、使う側で判断する。
   */
  const getActionJumpLauncherContentId = (logics: CampaignLogicSingle[], currentElement: SurveyElementJson, answerValue: SurveyAnswerRecord): string | undefined => {
    const currentModelLogic = getCurrentModelLogic(logics, currentElement)
    const actionDetail = evaluateLogic(currentModelLogic?.actions, answerValue)

    if (!actionDetail) {
      // If matching action is not found, return undefined
      return undefined
    }

    const jumpType = actionDetail.to.type
    switch (jumpType) {
    case 'launcher_content_thanks':
      return actionDetail.to.value
    case 'question':
      return undefined
    case 'default':
      return undefined
    default:
      throw new Error('Unknown jump type')
    }
  }

  /**
   * 次のlauncherContentIdを返す
   * @param actionJumpLauncherContentId - getActionJumpLauncherContentIdのreturn
   * @param campaignDefaultLauncherContentId - campaign.launcherContentId
   */
  const getNextLauncherContentId = (actionJumpLauncherContentId: string | undefined, campaignDefaultLauncherContentId: string | null | undefined): string | null => {
    if (actionJumpLauncherContentId) {
      return actionJumpLauncherContentId
    }

    return campaignDefaultLauncherContentId || null
  }

  return {
    getJustNextElementIndex,
    getActionJumpQuestionIndex,
    getActionJumpLauncherContentId,
    getNextLauncherContentId,
    getProgressiveWidth,
    evaluateLogic,
  }
}
